import { faChevronDown, faGasPump, faHand, faRightLeft, faSort, faTimes, faUpDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useBids, useCollectionActivity, useUsersActivity } from '@reservoir0x/reservoir-kit-ui'
import { AcceptBid } from 'components/buttons'
import CancelBid from 'components/buttons/CancelBid'
import EditBid from 'components/buttons/EditBid'
import LoadingSpinner from 'components/common/LoadingSpinner'
import {
  Box,
  Button,
  Flex,
  FormatCryptoCurrency,
  TableRow,
  Text,
  Tooltip,
} from 'components/primitives'
import { ChainContext } from 'context/ChainContextProvider'
import { useENSResolver, useMarketplaceChain, useTimeSince, useMounted } from 'hooks'
import Link from 'next/link'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { MutatorCallback } from 'swr'
import { useIntersectionObserver } from 'usehooks-ts'
import { formatDollar, formatNumber } from 'utils/numbers'
import { OnlyUserOrdersToggle } from './OnlyUserOrdersToggle'
import { zeroAddress } from 'viem'
import {
  DropdownMenuItem,
  DropdownMenuContent,
} from 'components/primitives/Dropdown'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useMediaQuery } from 'react-responsive'
import { MobileActivityFilters } from 'components/common/MobileActivityFilters'
import { FilterButton } from 'components/common/FilterButton'
import { UserActivityTable } from './UserActivityTable'
import { useRouter } from 'next/router'
import { useAccount } from 'wagmi'

type Props = {
    address : string | undefined
}

type ActivityTypes = Exclude<
  NonNullable<
    NonNullable<
      Exclude<Parameters<typeof useCollectionActivity>['0'], boolean>
    >['types']
  >,
  string
>

type Filters = {
  type: ArrayItemTypes<ActivityTypes>
  name: string
}[]

export const ItemActivity: FC<Props> = ({address}) => {
  const filters: Filters = [
    {
      type: 'sale',
      name: 'Sales',
    },
    {
      type: 'ask',
      name: 'Listings',
    },
    {
      type: 'bid',
      name: 'Offers',
    },
    {
      type: 'transfer',
      name: 'Transfers',
    },
    {
      type: 'mint',
      name: 'Mints',
    },
  ]
  const [filterData, setFilterData] = useState<ActivityTypes>(['sale'])
  const isMounted = useMounted()
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const loadMoreObserver = useIntersectionObserver(loadMoreRef, {})

  const isSmallDevice = useMediaQuery({ maxWidth: 905 }) && isMounted

  return (
    <Flex direction="column" css={{ gap: '$4', width: '100%' }}>
      <Flex
        direction="column"
        css={{
          padding: '24px',
          width: '100%',
          outline: 'solid 1px $gray7',
          margin: '1px',
        }}
      >
        <Flex
          direction="row"
          css={{
            gap: '$3',
            alignContent: 'center',
          }}
        >
          <Text
            css={{
              color: 'white',
              fontSize: '16px',
              fontWeight: 500,
              marginBottom: '24px',
            }}
          >
            <FontAwesomeIcon
              color="#697177"
              icon={faRightLeft}
              width="16"
              height="16"
              style={{ rotate: '90deg', marginRight: '10px' }}
            />
            Item Activity
          </Text>
        </Flex>
        <Box
          css={{
            padding: '0.5px',
            backgroundColor: '#464b50',
            marginBottom: '24px',
          }}
        ></Box>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              color="ghost"
              css={{
                height: '40px',
                padding: '16px 10px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#FFFFFF',
                gap: '24px',
                border: '1px solid var(--Materials-Vanadium, #2F2F32)',
                borderRadius: '5px',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              {isSmallDevice ? (
                <FontAwesomeIcon icon={faSort} width={16} height={16} />
              ) : (
                <>
                  <span>Filter</span>
                  <Box
                    css={{
                      transition: 'transform',
                      '[data-state=open] &': {
                        transform: 'rotate(180deg)',
                      },
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronDown} width={16} />
                  </Box>
                </>
              )}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenuContent
            css={{
              width: '95vw',
              mt: '$2',
              zIndex: 1000,
              maxWidth: '1400px',
            }}
          >
            {filters.map((filter) => (
              <DropdownMenuItem
                key={filter.type}
                css={{ py: '$3' }}
                onClick={() => {
                  if (filterData.findIndex((data) => data == filter.type) == -1) {
                    setFilterData([...filterData, filter.type])
                  }
                }}
                aria-label={`Sort by ${filter.type}`}
              >
                {filter.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu.Root>
        {filterData.length ? (
          <Flex
            className="hey"
            direction="row"
            wrap="wrap"
            css={{
              gap: '14px',
              // marginBottom: '24px',
            }}
          >
            {filterData.map((data, index) => {
              return (
                <Box
                  key={index}
                  css={{
                    backgroundColor: '$gray1',
                    py: '$2',
                    px: '$4',
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: '$gray6', // Darker shade on hover
                    },
                    gap: '$4',
                    mb: '20px',
                  }}
                  onClick={() => {
                    setFilterData(filterData.filter((item) => item != data))
                  }}
                >
                  {filters[filters.findIndex(item=>item.type == data)].name}
                  <FontAwesomeIcon
                    color="#697177"
                    icon={faTimes}
                    width="16"
                    height="16"
                    style={{ marginLeft: '10px' }}
                  />
                </Box>
              )
            })}
            <Box
              css={{
                py: '$2',
                px: '$4',
                borderRadius: '1rem',
                cursor: 'pointer',
                gap: '$4',
                mb: '20px',
                color: '#5BADFF',
              }}
              onClick={() => {
                setFilterData([])
              }}
            >
              Clear All
            </Box>
          </Flex>
        ) : (
          <></>
        )}
        <Box
          css={{
            padding: '0.5px',
            backgroundColor: '#464b50',
            marginBottom: '24px',
          }}
        ></Box>
        <Box
          css={{
            display: 'grid',
            // grid-template-columns: repeat(4, 1fr),
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '2px',
            fontWeight: 400,
            marginBottom: '24px',
            alignItems: 'center',

            '@bp600': {},
          }}
        >
          <Box
            css={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginLeft: '20px',
            }}
          >
            Event
          </Box>
          <Box
            css={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginLeft:"40px"
            }}
          >
            Unit Price
          </Box>
          <Box
            css={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Quantity
          </Box>
          <Box
            css={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            From
          </Box>
          <Box
            css={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            To
          </Box>
          <Box
            css={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Date
          </Box>
        </Box>
        <Flex
          direction="column"
          css={{
            padding: '24px',
            overflowY: 'auto',
            width: '100%',
            outline: 'solid 1px $gray7',
            margin: '1px',
            maxHeight : "300px"
          }}
        >
          <Box
            css={{
              flex: 1,
              gap: '$4',
            }}
          >
            <UserActivityTable user={address} activityTypes={filterData} />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}
